import React, { useState, useEffect, useCallback } from "react";
import api, { fmtFull } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Upload, Image as ImageIcon, BookOpen, AlertTriangle } from "lucide-react";
import { DateRangePicker } from "@/components/DateRangePicker";
import QuickDateButtons from "./QuickDateButtons";

export default function SoThuChiTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [view, setView] = useState("detail");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), type: "thu", description: "", amount: "", doi_tuong: "" });

  // Invoice upload
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [linkOpen, setLinkOpen] = useState(null); // invoice id being linked
  const [previewImg, setPreviewImg] = useState(null);
  const [pollRef, setPollRef] = useState(null);

  // Row detail popup
  const [selectedRow, setSelectedRow] = useState(null);
  const [chungtuData, setChungtuData] = useState(null);
  const [chungtuLoading, setChungtuLoading] = useState(false);
  const [manualUploading, setManualUploading] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/accounting/so-thu-chi?start=${start}&end=${end}&view=${view}`); setData(r.data); } catch {}
    finally { setLoading(false); }
  }, [start, end, view]);

  const fetchUnmatched = useCallback(async () => {
    try { const r = await api.get("/accounting/invoices/unmatched"); setUnmatched(r.data.invoices || []); } catch {}
  }, []);

  // When row is selected, fetch chungtu
  useEffect(() => {
    if (!selectedRow?.transaction_id) { setChungtuData(null); return; }
    setChungtuLoading(true);
    api.get(`/accounting/chungtu/${selectedRow.transaction_id}`).then(({ data: ct }) => {
      setChungtuData(ct);
    }).catch(() => setChungtuData(null)).finally(() => setChungtuLoading(false));
  }, [selectedRow]);

  const [fullscreenImg, setFullscreenImg] = useState(null);

  const handleManualUploadInPopup = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRow?.transaction_id) return;
    setManualUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const b64 = ev.target.result.split(",")[1];
        await api.post("/accounting/chungtu/manual-attach", {
          transaction_id: selectedRow.transaction_id,
          image_b64: b64,
          label: file.name,
        });
        // Re-fetch to get updated gallery
        const { data: ct } = await api.get(`/accounting/chungtu/${selectedRow.transaction_id}`);
        setChungtuData(ct);
        toast.success("Đã gắn chứng từ");
        setManualUploading(false);
        fetchData();
      };
      reader.readAsDataURL(file);
    } catch { setManualUploading(false); toast.error("Lỗi upload"); }
  };

  useEffect(() => { fetchData(); fetchUnmatched(); }, [fetchData, fetchUnmatched]);
  useEffect(() => { return () => { if (pollRef) clearInterval(pollRef); }; }, [pollRef]);

  const submit = async () => {
    if (!form.description || !form.amount) { toast.error("Nhập nội dung và số tiền"); return; }
    try {
      await api.post("/accounting/transactions", { ...form, amount: parseFloat(form.amount), area: "", category: form.type === "thu" ? "doanh_thu" : "van_hanh", payment_method: "tien_mat", note: form.doi_tuong });
      toast.success("Đã thêm"); setAddOpen(false); fetchData();
    } catch { toast.error("Lỗi"); }
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setBatchStatus(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data: d } = await api.post("/accounting/invoices/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
      setBatchId(d.batch_id);
      setBatchStatus({ status: "processing", total_pages: d.total_pages, processed: 0, results: [] });
      toast.success(d.message);
      // Poll progress
      const interval = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/accounting/invoices/batch/${d.batch_id}`);
          setBatchStatus(s);
          if (s.status === "completed") {
            clearInterval(interval); setPollRef(null);
            toast.success(`Xong: ${s.summary?.auto_matched || 0} tự khớp, ${s.summary?.suggested || 0} gợi ý, ${s.summary?.unmatched || 0} chưa khớp`);
            fetchData(); fetchUnmatched();
          }
        } catch {}
      }, 3000);
      setPollRef(interval);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleLink = async (invoiceId, transactionId) => {
    try {
      await api.post(`/accounting/invoices/${invoiceId}/link/${transactionId}`);
      toast.success("Đã liên kết"); setLinkOpen(null); fetchUnmatched(); fetchData();
    } catch { toast.error("Lỗi liên kết"); }
  };

  const typeLabels = { phieu_thu: "Phiếu Thu", phieu_chi: "Phiếu Chi", hoa_don: "Hóa đơn", receipt: "Receipt", bang_ke_mua_hang: "BK Mua hàng", bang_ke_tien_mat: "BK Tiền mặt", phieu_giao_hang: "Giao hàng", bao_gia: "Báo giá", khac: "Khác" };
  const typeColors = { phieu_thu: "text-emerald-400 bg-emerald-500/10", phieu_chi: "text-red-400 bg-red-500/10", hoa_don: "text-sky-400 bg-sky-500/10", receipt: "text-amber-400 bg-amber-500/10", bang_ke_tien_mat: "text-violet-400 bg-violet-500/10" };

  return (
    <div className="space-y-3" data-testid="acct-so-thu-chi">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-lime-400" /> Sổ Theo Dõi Thu Chi
        </h2>
        <div className="flex items-center gap-2">
          <DateRangePicker start={start} end={end} onChangeStart={setStart} onChangeEnd={setEnd} />
        <QuickDateButtons setStart={setStart} setEnd={setEnd} />
          <div className="flex bg-[#1e293b] border border-[#334155] rounded-md overflow-hidden">
            <button onClick={() => setView("detail")} className={`px-2 py-1 text-[10px] ${view === "detail" ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>Chi tiết</button>
            <button onClick={() => setView("summary")} className={`px-2 py-1 text-[10px] ${view === "summary" ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>Tổng hợp</button>
          </div>
          <label className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500 hover:bg-indigo-400 text-white rounded-md cursor-pointer text-[10px] font-semibold" data-testid="upload-invoice-btn">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Scan hóa đơn
            <input type="file" accept=".pdf,image/*" onChange={handleInvoiceUpload} className="hidden" disabled={uploading} />
          </label>
          <Button onClick={() => setAddOpen(true)} size="sm" className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-7 text-[10px] gap-1"><Plus className="w-3 h-3" /> Thêm</Button>
        </div>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Tồn đầu kỳ</p>
            <p className="text-sm font-bold text-sky-400">{fmtFull(data.ton_dau_ky || 0)}</p>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Tổng Thu</p>
            <p className="text-sm font-bold text-emerald-400">{fmtFull(data.total_thu || 0)}</p>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Tổng Chi</p>
            <p className="text-sm font-bold text-red-400">{fmtFull(data.total_chi || 0)}</p>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Tồn cuối kỳ</p>
            <p className="text-sm font-bold text-amber-400">{fmtFull(data.ton_cuoi_ky || 0)}</p>
          </div>
        </div>
      )}

      {/* Batch Processing Progress */}
      {batchStatus && batchStatus.status === "processing" && (
        <div className="bg-[#1e293b] border border-indigo-500/20 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-slate-300 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin text-indigo-400" /> Đang đọc hóa đơn...</span>
            <span className="text-[10px] text-indigo-400 font-bold">{batchStatus.processed}/{batchStatus.total_pages} trang</span>
          </div>
          <div className="h-1.5 bg-[#0f172a] rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${(batchStatus.processed / batchStatus.total_pages) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Batch Results Summary */}
      {batchStatus?.status === "completed" && batchStatus.results?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-200">Kết quả scan ({batchStatus.total_pages} trang)</span>
            <div className="flex gap-2">
              {batchStatus.summary?.auto_matched > 0 && <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[9px]">{batchStatus.summary.auto_matched} tự khớp</Badge>}
              {batchStatus.summary?.suggested > 0 && <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[9px]">{batchStatus.summary.suggested} gợi ý</Badge>}
              {batchStatus.summary?.unmatched > 0 && <Badge className="bg-red-500/20 text-red-400 border-0 text-[9px]">{batchStatus.summary.unmatched} chưa khớp</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 max-h-[150px] overflow-y-auto">
            {batchStatus.results.map((r, i) => (
              <div key={i} className="bg-[#0f172a] border border-[#334155]/50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-slate-500">Trang {r.page}</span>
                  <span className={`text-[8px] px-1 rounded ${typeColors[r.type] || "text-slate-400 bg-slate-500/10"}`}>{typeLabels[r.type] || r.type}</span>
                </div>
                <p className="text-[9px] text-slate-300 truncate mt-0.5">{r.summary || r.chung_tu}</p>
                {r.amount > 0 && <p className="text-[9px] font-bold text-slate-200">{fmtFull(r.amount)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Table */}
      {loading ? <Loader2 className="w-5 h-5 text-lime-400 animate-spin mx-auto" /> :
      data?.view === "summary" ? (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
          <table className="w-full text-[10px]">
            <thead><tr className="bg-[#0f172a] text-slate-400 uppercase">
              <th className="py-2 px-3 text-left font-medium">Ngày</th>
              <th className="py-2 px-3 text-right font-medium">Thu</th>
              <th className="py-2 px-3 text-right font-medium">Chi</th>
              <th className="py-2 px-3 text-right font-medium">Tồn</th>
              <th className="py-2 px-3 text-center font-medium">Giao dịch</th>
            </tr></thead>
            <tbody>
              {(data.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-[#334155]/30 hover:bg-[#0f172a]/50">
                  <td className="py-1.5 px-3 text-slate-300 font-mono">{r.date}</td>
                  <td className="py-1.5 px-3 text-right text-emerald-400 font-medium">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                  <td className="py-1.5 px-3 text-right text-red-400 font-medium">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                  <td className="py-1.5 px-3 text-right text-amber-400 font-bold">{fmtFull(r.ton)}</td>
                  <td className="py-1.5 px-3 text-center text-slate-500">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-x-auto">
          <table className="w-full text-[10px] min-w-[900px]">
            <thead><tr className="bg-[#0f172a] text-slate-400 uppercase">
              <th className="py-2 px-2 text-center font-medium w-10">STT</th>
              <th className="py-2 px-2 text-left font-medium w-20">Ngày</th>
              <th className="py-2 px-2 text-center font-medium w-16">CT Thu</th>
              <th className="py-2 px-2 text-center font-medium w-16">CT Chi</th>
              <th className="py-2 px-2 text-left font-medium">Nội dung</th>
              <th className="py-2 px-2 text-right font-medium w-24">Thu</th>
              <th className="py-2 px-2 text-right font-medium w-24">Chi</th>
              <th className="py-2 px-2 text-right font-medium w-28">Tồn</th>
              <th className="py-2 px-2 text-left font-medium w-28">Đối tượng</th>
              <th className="py-2 px-2 text-left font-medium w-20">Địa chỉ</th>
              <th className="py-2 px-2 text-center font-medium w-14">CT</th>
            </tr></thead>
            <tbody>
              {(data.rows || []).map((r, i) => (
                r.is_total ? (
                  <tr key={i} className="bg-[#334155]/20 border-t border-[#334155] font-bold">
                    <td colSpan={5} className="py-1.5 px-2 text-red-400">{r.description}</td>
                    <td className="py-1.5 px-2 text-right text-emerald-400">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                    <td className="py-1.5 px-2 text-right text-red-400">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                    <td className="py-1.5 px-2 text-right text-amber-400">{fmtFull(r.ton)}</td>
                    <td colSpan={3}></td>
                  </tr>
                ) : (
                  <tr key={i} className="border-t border-[#334155]/20 hover:bg-indigo-500/5 cursor-pointer transition-colors" onClick={() => setSelectedRow(r)} data-testid={`stc-row-${i}`}>
                    <td className="py-1.5 px-2 text-center text-slate-500">{r.stt}</td>
                    <td className="py-1.5 px-2 text-slate-400 font-mono">{r.date?.slice(5)}</td>
                    <td className="py-1.5 px-2 text-center text-emerald-400 font-mono">{r.chung_tu_thu}</td>
                    <td className="py-1.5 px-2 text-center text-red-400 font-mono">{r.chung_tu_chi}</td>
                    <td className="py-1.5 px-2 text-slate-300 truncate max-w-[200px]">{r.noi_dung}</td>
                    <td className="py-1.5 px-2 text-right text-emerald-400">{r.thu > 0 ? fmtFull(r.thu) : ""}</td>
                    <td className="py-1.5 px-2 text-right text-red-400">{r.chi > 0 ? fmtFull(r.chi) : ""}</td>
                    <td className="py-1.5 px-2 text-right text-slate-200 font-medium">{fmtFull(r.ton)}</td>
                    <td className="py-1.5 px-2 text-slate-300 truncate max-w-[100px]">{r.doi_tuong}</td>
                    <td className="py-1.5 px-2 text-slate-500 truncate max-w-[80px] text-[9px]">{r.dia_chi}</td>
                    <td className="py-1.5 px-2 text-center">
                      {r.has_invoice ? (
                        <span className="inline-flex items-center gap-0.5 text-violet-400" title="Có chứng từ đính kèm">
                          <ImageIcon className="w-3.5 h-3.5" />
                          <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1L10 5.5L15 6.5L11.5 10L12.5 15L8 12.5L3.5 15L4.5 10L1 6.5L6 5.5L8 1Z"/></svg>
                        </span>
                      ) : (
                        <span className="text-slate-600 text-[9px]">—</span>
                      )}
                    </td>
                  </tr>
                )
              ))}
              {(!data.rows || data.rows.length === 0) && (
                <tr><td colSpan={10} className="py-8 text-center text-slate-500">Chưa có dữ liệu. Nhập file Excel hoặc thêm thủ công.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Unmatched Invoices Section */}
      {unmatched.length > 0 && (
        <div className="bg-[#1e293b] border border-amber-500/20 rounded-xl p-4 space-y-3" data-testid="unmatched-invoices">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Hóa đơn chưa liên kết ({unmatched.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {unmatched.map(inv => (
              <div key={inv.id} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2" data-testid={`unmatched-${inv.id}`}>
                <div className="flex gap-2">
                  {/* Thumbnail */}
                  <div className="w-14 h-14 bg-[#334155] rounded overflow-hidden shrink-0 cursor-pointer" onClick={() => setPreviewImg(`${API_BASE}${inv.image_url}`)}>
                    <img src={`${API_BASE}${inv.image_url}`} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className={`text-[8px] px-1 rounded ${typeColors[inv.ocr_type] || "text-slate-400 bg-slate-500/10"}`}>{typeLabels[inv.ocr_type] || inv.ocr_type}</span>
                      {inv.match_status === "suggested" && <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[8px]">Gợi ý</Badge>}
                    </div>
                    <p className="text-[9px] text-slate-300 truncate">{inv.ocr_data?.description || inv.ocr_data?.summary || "Không có mô tả"}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {inv.ocr_data?.amount > 0 && <span className="text-[9px] font-bold text-slate-200">{fmtFull(inv.ocr_data.amount)}</span>}
                      {inv.ocr_data?.date && <span className="text-[8px] text-slate-500">{inv.ocr_data.date}</span>}
                      {inv.ocr_data?.chung_tu && <span className="text-[8px] text-indigo-400 font-mono">{inv.ocr_data.chung_tu}</span>}
                    </div>
                  </div>
                </div>
                <Button onClick={() => setLinkOpen(inv)} size="sm" variant="outline" className="w-full h-6 text-[9px] border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  Liên kết vào Sổ Thu Chi
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Dialog */}
      {linkOpen && (
        <Dialog open={!!linkOpen} onOpenChange={() => setLinkOpen(null)}>
          <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg">
            <DialogHeader><DialogTitle className="text-amber-400 text-sm">Liên kết hóa đơn → Sổ Thu Chi</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <p className="text-[10px] text-slate-400">Hóa đơn: <span className="text-slate-200">{linkOpen.ocr_data?.description} — {fmtFull(linkOpen.ocr_data?.amount || 0)}</span></p>
              <p className="text-[10px] text-slate-400">Chọn dòng giao dịch phù hợp:</p>
              <div className="max-h-[300px] overflow-y-auto space-y-1">
                {(data?.rows || []).filter(r => !r.is_total).slice(0, 50).map((r, i) => (
                  <button key={i} onClick={() => handleLink(linkOpen.id, r.transaction_id)} className="w-full text-left bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2 hover:border-amber-500/40 transition-colors">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] text-slate-300 truncate max-w-[200px]">{r.noi_dung}</span>
                      <span className={`text-[9px] font-bold ${r.thu > 0 ? "text-emerald-400" : "text-red-400"}`}>{r.thu > 0 ? `+${fmtFull(r.thu)}` : `-${fmtFull(r.chi)}`}</span>
                    </div>
                    <span className="text-[8px] text-slate-500">{r.date} · {r.chung_tu_thu || r.chung_tu_chi || ""} · {r.doi_tuong}</span>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Image Preview */}
      {previewImg && (
        <Dialog open={!!previewImg} onOpenChange={() => setPreviewImg(null)}>
          <DialogContent className="bg-[#0f172a] border-[#334155] max-w-3xl p-2">
            <img src={previewImg} alt="Hóa đơn" className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      )}

      {/* Add Transaction Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-lime-400">Thêm giao dịch</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Ngày</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
              <div><Label className="text-slate-400 text-[10px]">Loại</Label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full mt-0.5 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-xs text-slate-200">
                  <option value="thu">Thu</option><option value="chi">Chi</option>
                </select></div>
            </div>
            <div><Label className="text-slate-400 text-[10px]">Nội dung *</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Số tiền *</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
              <div><Label className="text-slate-400 text-[10px]">Đối tượng</Label><Input value={form.doi_tuong} onChange={e => setForm({...form, doi_tuong: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            </div>
            <Button onClick={submit} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold h-9 text-xs">Lưu giao dịch</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Row Detail Popup */}
      {selectedRow && !selectedRow.is_total && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedRow(null)} data-testid="stc-detail-popup">
          <div onClick={e => e.stopPropagation()} className="bg-[#1e293b] border border-[#475569] rounded-2xl shadow-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-100">Chi tiết giao dịch #{selectedRow.stt}</h3>
              <button onClick={() => setSelectedRow(null)} className="text-slate-500 hover:text-slate-200 text-lg">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Ngày</p>
                <p className="text-slate-200 font-medium">{selectedRow.date?.slice(8,10)}/{selectedRow.date?.slice(5,7)}/{selectedRow.date?.slice(0,4)}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Số chứng từ</p>
                <p className={`font-bold ${selectedRow.chung_tu_thu ? "text-emerald-400" : "text-red-400"}`}>{selectedRow.chung_tu_thu || selectedRow.chung_tu_chi || "-"}</p>
              </div>
              <div className="col-span-2 bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Nội dung</p>
                <p className="text-slate-200">{selectedRow.noi_dung}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <p className="text-emerald-400 text-[9px] uppercase mb-1">Số tiền THU</p>
                <p className="text-emerald-400 font-bold text-lg">{selectedRow.thu > 0 ? fmtFull(selectedRow.thu) : "-"}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-[9px] uppercase mb-1">Số tiền CHI</p>
                <p className="text-red-400 font-bold text-lg">{selectedRow.chi > 0 ? fmtFull(selectedRow.chi) : "-"}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-400 text-[9px] uppercase mb-1">Tồn quỹ</p>
                <p className="text-amber-400 font-bold text-lg">{fmtFull(selectedRow.ton)}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Loại</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${selectedRow.thu > 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                  {selectedRow.thu > 0 ? "Phiếu thu" : "Phiếu chi"}
                </span>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Đối tượng</p>
                <p className="text-slate-200 font-medium">{selectedRow.doi_tuong || "-"}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-3">
                <p className="text-slate-500 text-[9px] uppercase mb-1">Địa chỉ / Chức vụ</p>
                <p className="text-slate-200">{selectedRow.dia_chi || "-"}</p>
              </div>
              {selectedRow.ghi_chu && (
                <div className="col-span-2 bg-[#0f172a] rounded-lg p-3">
                  <p className="text-slate-500 text-[9px] uppercase mb-1">Ghi chú</p>
                  <p className="text-slate-300 text-[10px]">{selectedRow.ghi_chu}</p>
                </div>
              )}
            </div>

            {/* Chứng từ image section - GALLERY MODE */}
            <div className="border-t border-[#334155] pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-400 uppercase font-bold">
                  Hình ảnh chứng từ {chungtuData?.total > 0 && `(${chungtuData.total})`}
                </p>
                <label className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-medium cursor-pointer transition-colors ${manualUploading ? "bg-slate-700 text-slate-500" : "bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/30"}`}>
                  {manualUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {manualUploading ? "Đang tải..." : "Thêm ảnh"}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleManualUploadInPopup} disabled={manualUploading} />
                </label>
              </div>

              {chungtuLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 text-slate-500 animate-spin" /></div>
              ) : chungtuData?.images?.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {chungtuData.images.map((img, idx) => (
                    <div key={idx} className="relative group cursor-pointer" onClick={() => setFullscreenImg(img)}>
                      <img src={`data:image/jpeg;base64,${img.image_b64}`} alt={img.label}
                        className="w-full h-20 object-cover rounded-lg border border-[#334155] hover:border-violet-500/50 transition-colors" />
                      <span className={`absolute top-1 right-1 px-1 py-0.5 rounded text-[7px] font-bold ${img.matched_by === "ai" || img.matched_by === "ai_created" ? "bg-violet-500/80 text-white" : "bg-sky-500/80 text-white"}`}>
                        {img.matched_by === "ai" || img.matched_by === "ai_created" ? "AI" : "Tay"}
                      </span>
                      <p className="text-[8px] text-slate-500 mt-0.5 truncate">{img.label}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#334155] rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-xs">Chưa có chứng từ đính kèm</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen image viewer */}
      {fullscreenImg && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={() => setFullscreenImg(null)} data-testid="fullscreen-viewer">
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setFullscreenImg(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-[#1e293b] border border-[#475569] rounded-full flex items-center justify-center text-slate-300 hover:text-white z-10">&times;</button>
            <img src={`data:image/jpeg;base64,${fullscreenImg.image_b64}`} alt={fullscreenImg.label}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl" />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-400">{fullscreenImg.label}</p>
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${fullscreenImg.matched_by === "ai" || fullscreenImg.matched_by === "ai_created" ? "bg-violet-500/20 text-violet-400" : "bg-sky-500/20 text-sky-400"}`}>
                {fullscreenImg.matched_by === "ai" || fullscreenImg.matched_by === "ai_created" ? "AI tự động" : "Gắn tay"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

